import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const testimonials = [
  {
    name: '小樱',
    role: '同人画师 / 5年创作经验',
    content: '这个网站的脚本写作教程真的太实用了！我之前一直卡在故事结构上，看完几篇文章后思路豁然开朗，现在已经完成了第一部同人志。',
    avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20of%20young%20female%20artist%20with%20warm%20smile%2C%20soft%20lighting%2C%20clean%20background&width=100&height=100&seq=avatar-01&orientation=squarish',
  },
  {
    name: '阿杰',
    role: '漫画初学者 / 学生',
    content: '作为刚入坑的新手，分镜技巧板块帮我少走了很多弯路。尤其是镜头语言那篇，让我对漫画叙事有了全新的理解。',
    avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20of%20young%20male%20student%20with%20glasses%2C%20friendly%20expression%2C%20clean%20background&width=100&height=100&seq=avatar-02&orientation=squarish',
  },
  {
    name: '琉璃',
    role: '自由插画师 / 同人社团主催',
    content: '发布平台汇总太全了！我之前只知道Pixiv，通过这里的指南又发现了几个不错的平台。版权知识的讲解也很到位，推荐给所有同人创作者。',
    avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20of%20female%20illustrator%20with%20long%20hair%2C%20gentle%20smile%2C%20clean%20background&width=100&height=100&seq=avatar-03&orientation=squarish',
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9s8o4me3oq4jqumu74g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 同人创作指南',
    description: '了解同人创作指南的品牌故事、团队愿景和联系方式。我们致力于为同人漫画创作者提供最全面、最实用的创作资源。',
    url: 'https://doujin-guide.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '同人创作指南',
    url: 'https://doujin-guide.com',
    description: '专注同人漫画创作技巧、发布平台与版权知识的综合性指南网站。',
    email: 'contact@doujin-guide.com',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Page Header */}
          <section className="relative w-full py-16 md:py-24 overflow-hidden bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="max-w-3xl">
                <div className="mb-4">
                  <Breadcrumb
                    items={[
                      { label: '首页', href: '/' },
                      { label: '关于我们' },
                    ]}
                  />
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  关于我们
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  同人创作指南致力于为二次元爱好者提供最全面、最实用的同人漫画创作资源。从脚本写作到发布推广，我们陪伴每一位创作者的成长之路。
                </p>
              </div>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4">
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    同人创作指南诞生于一群热爱同人漫画的创作者之手。我们深知，在同人创作的路上，每个创作者都会遇到各种困惑：如何写出引人入胜的故事？怎样设计流畅的分镜？应该选择哪个平台发布作品？版权问题又该如何处理？
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    正是这些共同的困惑，促成了这个网站的诞生。我们希望通过系统化的知识整理和实战经验分享，帮助每一位同人漫画创作者少走弯路，更快地找到属于自己的创作之路。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    自建站以来，我们已经发布了数十篇高质量的同人漫画创作指南文章，覆盖了脚本写作、分镜设计、绘制技法、发布平台和版权须知五大核心领域。我们的内容被众多同人创作者推荐和引用，已成为中文同人圈内备受信赖的创作知识平台。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20artist%20creative%20team%20collaborating%20in%20cozy%20studio%2C%20sketchbooks%20and%20drawing%20tablets%20on%20desks%2C%20warm%20lighting%2C%20creative%20atmosphere%2C%20teamwork%20vibe&width=600&height=450&seq=about-brand-01&orientation=landscape"
                  alt="同人创作指南团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
                我们的理念
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-accent-100 mx-auto mb-4">
                    <i className="ri-heart-line text-xl text-accent-500" />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-3">热爱驱动</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们相信，最优秀的同人作品都源于对原作的真挚热爱。保持这份热爱，是创作的核心动力。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-accent-100 mx-auto mb-4">
                    <i className="ri-share-line text-xl text-accent-500" />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-3">知识共享</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    创作经验不应该被埋没。我们致力于将优质的同人创作知识免费分享给每一位需要的人。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-accent-100 mx-auto mb-4">
                    <i className="ri-community-line text-xl text-accent-500" />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-3">社区共建</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    同人文化因交流而繁荣。我们鼓励创作者相互学习、分享心得，共同推动中文同人创作的发展。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-1 mt-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-accent-500" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600">
                  有任何问题或建议？欢迎通过以下方式联系我们
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入你的留言内容（最多500字）"
                  />
                  <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                </div>

                <div className="website_alt_wrapper" style={{ position: 'absolute', left: '-9999px', opacity: 0 }}>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>

                {formStatus !== 'idle' && (
                  <div
                    className={`mt-4 p-3 rounded-md text-sm ${
                      formStatus === 'success'
                        ? 'bg-accent-100 text-accent-900'
                        : 'bg-accent-100 text-accent-900'
                    }`}
                  >
                    {formMessage}
                  </div>
                )}
              </form>

              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <div className="text-center p-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-accent-100 mx-auto mb-3">
                    <i className="ri-mail-line text-accent-500" />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm text-foreground-950 font-medium">contact@doujin-guide.com</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-accent-100 mx-auto mb-3">
                    <i className="ri-wechat-line text-accent-500" />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                  <p className="text-sm text-foreground-950 font-medium">同人创作指南</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-accent-100 mx-auto mb-3">
                    <i className="ri-bilibili-line text-accent-500" />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">B站账号</p>
                  <p className="text-sm text-foreground-950 font-medium">同人创作指南官方</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}